import { useCallback, useEffect, useRef, useState } from 'react';
import { sitePath, WORKS_GALLERY_PATH, INSIGHTS_HUB_PATH } from '@/lib/site-urls';
import { CurvedMenu, type CurvedMenuItem } from '@/components/ui/curved-menu';
import { FloatingContact } from '@/components/landing/FloatingContact';
import '@/styles.css';

export function LandingNav() {
  const navRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 1024 : false,
  );
  const savedScrollYRef = useRef(0);
  const [debug, setDebug] = useState(false);

  const setMenuOpen = useCallback((next: boolean) => {
    setOpen(next);
    const nav = navRef.current;
    const toggle = nav?.querySelector('.nav-toggle');
    if (nav) nav.classList.toggle('is-mobile-open', next);
    if (toggle) {
      toggle.classList.toggle('is-open', next);
      toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
      toggle.setAttribute('aria-label', next ? 'Close menu' : 'Open menu');
    }
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
      const root = document.documentElement;
      const body = document.body;
      if (next) {
        savedScrollYRef.current = window.scrollY || root.scrollTop || 0;
        root.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${savedScrollYRef.current}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
      } else {
        root.style.overflow = '';
        body.style.overflow = '';
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        window.scrollTo(0, savedScrollYRef.current);
      }
    }

    if (typeof window !== 'undefined') {
      const debugOn = new URLSearchParams(window.location.search).has('navdebug');
      if (debugOn) {
        // eslint-disable-next-line no-console
        console.log('[LandingNav] setMenuOpen', {
          next,
          innerWidth: window.innerWidth,
          isMobile: window.innerWidth <= 1024,
          navHasClassOpen: nav?.classList.contains('is-mobile-open'),
        });
      }
      (window as any).__tcNav = {
        open: next,
        innerWidth: window.innerWidth,
        isMobile: window.innerWidth <= 1024,
        ts: Date.now(),
      };
    }
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const updateIsMobile = () => setIsMobile(window.innerWidth <= 1024);
    updateIsMobile();
    setDebug(new URLSearchParams(window.location.search).has('navdebug'));

    const onScroll = () => {
      nav.classList.toggle('is-scrolled', window.scrollY > 12);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);

    const onDocClick = (e: MouseEvent) => {
      if (!nav.classList.contains('is-mobile-open')) return;
      if (nav.contains(e.target as Node)) return;
      setMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);

    const onResize = () => {
      updateIsMobile();
      if (window.innerWidth > 1024) setMenuOpen(false);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onDocClick);
      window.removeEventListener('resize', onResize);
    };
  }, [setMenuOpen]);

  return (
    <nav ref={navRef} className="main-nav" role="navigation" id="main-nav">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <div className="nav-container">
        <a href="/" className="nav-logo" aria-label="ThinkinCode home">
          <span className="brand-code">
            <span className="brand-paren">thinkinCode(</span>
            <img className="nav-logo-mark brand-emblem" src="/thinkincode-logo.png" alt="ThinkinCode emblem" />
            <span className="brand-paren">);</span>
          </span>
        </a>
        <button
          type="button"
          className="nav-toggle"
          aria-label="Open menu"
          aria-expanded="false"
          aria-controls="nav-menu"
          onClick={() => setMenuOpen(!navRef.current?.classList.contains('is-mobile-open'))}
        >
          <svg
            className="nav-toggle-icon"
            strokeWidth="2.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 32 32"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path
              className="nav-toggle-icon-main"
              d="M27 10 13 10C10.8 10 9 8.2 9 6 9 3.5 10.8 2 13 2 15.2 2 17 3.8 17 6L17 26C17 28.2 18.8 30 21 30 23.2 30 25 28.2 25 26 25 23.8 23.2 22 21 22L7 22"
            />
            <path d="M7 16 27 16" />
          </svg>
        </button>
        <ul className="nav-menu" id="nav-menu">
          <li>
            <a href="/" className="nav-link" onClick={() => setMenuOpen(false)}>
              Home
            </a>
          </li>
          <li>
            <a href={sitePath('/services')} className="nav-link" onClick={() => setMenuOpen(false)}>
              Services
            </a>
          </li>
          <li>
            <a href={sitePath('/portfolio')} className="nav-link" onClick={() => setMenuOpen(false)}>
              Case Studies
            </a>
          </li>
          <li>
            <a href={WORKS_GALLERY_PATH} className="nav-link" onClick={() => setMenuOpen(false)}>
              Our Works
            </a>
          </li>
          <li>
            <a href={sitePath('/about')} className="nav-link" onClick={() => setMenuOpen(false)}>
              About
            </a>
          </li>
          <li>
            <a href={INSIGHTS_HUB_PATH} className="nav-link" onClick={() => setMenuOpen(false)}>
              Insights
            </a>
          </li>
          <li>
            <a href={sitePath('/contact')} className="nav-link" onClick={() => setMenuOpen(false)}>
              Contact
            </a>
          </li>
        </ul>
      </div>

      {debug ? (
        <div
          className="fixed bottom-3 left-3 z-[20000] rounded-md bg-black px-3 py-2 text-xs text-white md:hidden"
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
        >
          <div>LandingNav debug</div>
          <div>open: {String(open)}</div>
          <div>isMobile: {String(isMobile)}</div>
          <div>width: {typeof window !== 'undefined' ? window.innerWidth : 'n/a'}</div>
        </div>
      ) : null}

      <CurvedMenu
        open={open}
        onClose={() => setMenuOpen(false)}
        items={
          [
            { heading: 'Home', href: '/', subheading: 'Welcome to ThinkinCode' },
            { heading: 'Services', href: sitePath('/services'), subheading: 'What we offer' },
            { heading: 'Case Studies', href: sitePath('/portfolio'), subheading: 'Written project breakdowns' },
            { heading: 'Our Works', href: WORKS_GALLERY_PATH, subheading: 'Interactive project gallery' },
            { heading: 'About', href: sitePath('/about'), subheading: 'Who we are' },
            { heading: 'Insights', href: INSIGHTS_HUB_PATH, subheading: 'Guides & resources' },
            { heading: 'Contact', href: sitePath('/contact'), subheading: "Let's talk" },
          ] satisfies CurvedMenuItem[]
        }
      />
      <FloatingContact />
    </nav>
  );
}
